import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { RECORDINGS_BUCKET } from '@/lib/upload'
import type { Recording } from '@/types/database'

/**
 * Recordings, after Brian's "videos live in Photos" (record.html v1.9 and v3.1,
 * Oct 21-22 2026).
 *
 * A take is never uploaded. Save video + copy caption puts it in the phone's
 * Photos (on the web, it downloads), and GoGrov keeps one row per story so the
 * story moves on in Library: To record -> Recorded -> Posted. Those rows have
 * `on_device = true` and no `storage_path` (migration 20240039_record.sql).
 *
 * For Library (the contract other screens rely on):
 *   - A story is **Recorded** when it has any `recordings` row.
 *   - A story is **Posted** when one of its rows has `posted_at`.
 *   - `useRecordedStories()` gives both per story id in one query.
 *   - `useMarkRecorded()` / `useMarkPosted()` are what Record calls; both are
 *     safe to call again (one device row per story, updated in place).
 *   - Old uploaded takes (`on_device = false`) still play through
 *     `useSignedRecordingUrl()`; a device-only row has nothing to play
 *     (`isDeviceOnly(r)`), the video is in the creator's Photos.
 */

/** The row as it is after 20240039_record.sql. */
export interface RecordingRow extends Omit<Recording, 'storage_path'> {
  storage_path: string | null
  on_device: boolean
  posted_at: string | null
  posted_caption: string | null
}

export const isDeviceOnly = (r: Pick<RecordingRow, 'on_device' | 'storage_path'>) =>
  r.on_device || !r.storage_path

export interface MarkRecordedInput {
  storyId: string
  /** The take went into Photos (Save video + copy caption). */
  saved?: boolean
  /** The take's length, when there is one. */
  durationSeconds?: number | null
  mimeType?: string | null
}

const invalidateAll = (qc: ReturnType<typeof useQueryClient>, clientId: string | null, storyId?: string) => {
  void qc.invalidateQueries({ queryKey: ['library', clientId] })
  if (storyId) void qc.invalidateQueries({ queryKey: ['story-progress', storyId] })
  void qc.invalidateQueries({ queryKey: ['recordings', clientId] })
  void qc.invalidateQueries({ queryKey: ['recording-library', clientId] })
  void qc.invalidateQueries({ queryKey: ['recorded-stories', clientId] })
}

/**
 * The Library's story_progress row (migration 20240035, the Library area) is
 * kept in step too, best effort: Library reads either.
 */
async function progress(clientId: string, storyId: string, patch: Record<string, string>) {
  try {
    await supabase.from('story_progress' as never)
      .upsert({ story_id: storyId, client_id: clientId, ...patch } as never, { onConflict: 'story_id' })
  } catch { /* the recordings row is the record */ }
}

/**
 * Marks a story Recorded. One device row per story: a second call (another
 * take saved) refreshes its length rather than adding rows.
 */
export async function markRecorded(
  clientId: string,
  userId: string | null,
  { storyId, durationSeconds = null, mimeType = null, saved = false }: MarkRecordedInput,
): Promise<RecordingRow> {
  const at = new Date().toISOString()
  await progress(clientId, storyId, saved ? { recorded_at: at, saved_at: at } : { recorded_at: at })
  const { data: existing, error: readErr } = await supabase
    .from('recordings')
    .select('*')
    .eq('client_id', clientId)
    .eq('story_id', storyId)
    .eq('on_device', true)
    .order('created_at', { ascending: false })
    .limit(1)
  if (readErr) throw readErr
  if (existing && existing.length) return existing[0] as unknown as RecordingRow

  const row = {
    client_id: clientId,
    story_id: storyId,
    user_id: userId,
    storage_path: null,
    on_device: true,
    mime_type: mimeType || 'video/mp4',
    size_bytes: null,
    duration_seconds: durationSeconds == null ? null : Math.round(durationSeconds),
  }
  const { data, error } = await supabase
    .from('recordings')
    // The generated insert type predates 20240039 (storage_path required).
    .insert(row as never)
    .select('*')
    .single()
  if (error) throw error
  return data as unknown as RecordingRow
}

/** Record calls this when a take is saved, and on Done. */
export function useMarkRecorded() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const qc = useQueryClient()
  return useMutation<RecordingRow, Error, MarkRecordedInput>({
    mutationFn: async (input) => {
      if (!clientId) throw new Error('No client selected.')
      return markRecorded(clientId, userId, input)
    },
    onSuccess: (_r, v) => invalidateAll(qc, clientId, v.storyId),
  })
}

/** "I posted it": the story files under Library > Posted with its caption. */
export function useMarkPosted() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const qc = useQueryClient()
  return useMutation<void, Error, { storyId: string; caption?: string | null }>({
    mutationFn: async ({ storyId, caption = null }) => {
      if (!clientId) throw new Error('No client selected.')
      const row = await markRecorded(clientId, userId, { storyId })
      const { error } = await supabase
        .from('recordings')
        .update({ posted_at: new Date().toISOString(), posted_caption: caption } as never)
        .eq('id', row.id)
      if (error) throw error
      await progress(clientId, storyId, { posted_at: new Date().toISOString() })
    },
    onSuccess: (_r, v) => invalidateAll(qc, clientId, v.storyId),
  })
}

export interface RecordedState {
  recordedAt: string
  postedAt: string | null
  postedCaption: string | null
}

/** Every recorded story for the client: story id -> when recorded / posted. */
export function useRecordedStories() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['recorded-stories', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recordings')
        .select('story_id, created_at, posted_at, posted_caption')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      const out: Record<string, RecordedState> = {}
      for (const r of (data ?? []) as unknown as Pick<RecordingRow, 'story_id' | 'created_at' | 'posted_at' | 'posted_caption'>[]) {
        const s = out[r.story_id] ?? { recordedAt: r.created_at, postedAt: null, postedCaption: null }
        if (r.posted_at && (!s.postedAt || r.posted_at > s.postedAt)) { s.postedAt = r.posted_at; s.postedCaption = r.posted_caption }
        out[r.story_id] = s
      }
      return out
    },
  })
}

/** Recordings for a story, newest first. */
export function useStoryRecordings(storyId: string | null) {
  const clientId = useEffectiveClientId()

  return useQuery({
    queryKey: ['recordings', clientId, storyId],
    enabled: !!clientId && !!storyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recordings')
        .select('*')
        .eq('client_id', clientId!)
        .eq('story_id', storyId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as RecordingRow[]
    },
  })
}

/**
 * Every recording for the client, newest first, with its story attached.
 *
 * The join is the point: a recording on its own is an opaque file, and what the
 * user remembers is the story they read.
 */
export function useRecordingLibrary() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['recording-library', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recordings')
        .select('*, stories(id, headline, category, source_name)')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as LibraryRecording[]
    },
  })
}

export interface LibraryRecording extends Recording {
  on_device?: boolean
  posted_at?: string | null
  posted_caption?: string | null
  stories: {
    id: string
    headline: string
    category: string
    source_name: string | null
  } | null
}

/**
 * A short lived signed URL for playback or download of an OLD uploaded take.
 * Device-only rows have no file (the video is in the creator's Photos).
 */
export function useSignedRecordingUrl() {
  return useMutation({
    mutationFn: async (storagePath: string) => {
      if (!storagePath) throw new Error('This video is in your Photos, not in GoGrov.')
      const { data, error } = await supabase.storage
        .from(RECORDINGS_BUCKET)
        .createSignedUrl(storagePath, 60 * 60)
      if (error) throw error
      return data.signedUrl
    },
  })
}

/**
 * Deletes a recording, file first (when it has one).
 *
 * Order matters: removing the row first would orphan the object in the bucket
 * with nothing left pointing at it, and storage is billed either way.
 */
export function useDeleteRecording() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recording: { id: string; storage_path: string | null }) => {
      if (recording.storage_path) {
        const { error: storageErr } = await supabase.storage
          .from(RECORDINGS_BUCKET)
          .remove([recording.storage_path])
        // A missing object should not block removing the row: the user asked for
        // this to be gone, and a dangling row is worse than a dangling file.
        if (storageErr && !/not found/i.test(storageErr.message)) throw storageErr
      }

      const { error } = await supabase.from('recordings').delete().eq('id', recording.id)
      if (error) throw error
    },
    onSuccess: () => {
      invalidateAll(queryClient, clientId)
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** Bytes currently held in the recordings bucket for this client (old uploads only). */
export function useStorageUsed() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['storage-used', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      // Summed over rows rather than listed from the bucket: the rows are the
      // source of truth for what belongs to this client, and listing storage
      // would not respect RLS the same way. Device rows carry no size.
      const { data, error } = await supabase
        .from('recordings').select('size_bytes').eq('client_id', clientId!)
      if (error) throw error
      return (data ?? []).reduce((n, r) => n + (r.size_bytes ?? 0), 0)
    },
  })
}
